import React, { useState, useMemo } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const MerchantPerformanceTable = ({ data, loading = false }) => {
  const [sortConfig, setSortConfig] = useState({ key: 'revenue', direction: 'desc' });
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedMerchants, setSelectedMerchants] = useState([]);
  const itemsPerPage = 10;

  const handleSort = (key) => {
    setSortConfig({
      key,
      direction: sortConfig?.key === key && sortConfig?.direction === 'asc' ? 'desc' : 'asc'
    });
  };

  const sortedData = React.useMemo(() => {
    const sorted = [...data]?.sort((a, b) => {
      if (sortConfig?.key === 'revenue' || sortConfig?.key === 'transactionVolume') {
        return sortConfig?.direction === 'asc' 
          ? a?.[sortConfig?.key] - b?.[sortConfig?.key]
          : b?.[sortConfig?.key] - a?.[sortConfig?.key];
      }
      if (sortConfig?.key === 'lastActivity') {
        return sortConfig?.direction === 'asc'
          ? new Date(a[sortConfig.key]) - new Date(b[sortConfig.key])
          : new Date(b[sortConfig.key]) - new Date(a[sortConfig.key]);
      }
      return 0;
    });
    return sorted;
  }, [data, sortConfig]);

  const paginatedData = sortedData?.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const totalPages = Math.ceil(sortedData?.length / itemsPerPage);

  const getRiskBadge = (level) => {
    const configs = {
      low: { color: 'bg-success/10 text-success border-success/20', icon: 'CheckCircle', label: 'Low Risk' },
      medium: { color: 'bg-warning/10 text-warning border-warning/20', icon: 'AlertCircle', label: 'Medium Risk' },
      high: { color: 'bg-error/10 text-error border-error/20', icon: 'AlertTriangle', label: 'High Risk' }
    };
    const config = configs?.[level] || configs?.low;
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium border ${config?.color}`}>
        <Icon name={config?.icon} size={12} />
        {config?.label}
      </span>
    );
  };

  const getStatusBadge = (status) => {
    const configs = {
      active: { color: 'bg-success/10 text-success border-success/20', label: 'Active' },
      inactive: { color: 'bg-muted text-muted-foreground border-border', label: 'Inactive' },
      suspended: { color: 'bg-error/10 text-error border-error/20', label: 'Suspended' }
    };
    const config = configs?.[status] || configs?.active;
    return (
      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${config?.color}`}>
        {config?.label}
      </span>
    );
  };

  const formatCurrency = (amount) => {
    return `Rp ${amount?.toLocaleString('id-ID')}`;
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date?.toLocaleDateString('id-ID', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const handleSelectMerchant = (merchantId) => {
    setSelectedMerchants(prev => 
      prev?.includes(merchantId) 
        ? prev?.filter(id => id !== merchantId)
        : [...prev, merchantId]
    );
  };

  const handleSelectAll = () => {
    if (selectedMerchants?.length === paginatedData?.length) {
      setSelectedMerchants([]);
    } else {
      setSelectedMerchants(paginatedData?.map(m => m?.id));
    }
  };

  if (loading) {
    return (
      <div className="bg-card border border-border rounded-xl p-4 md:p-6 shadow-sm animate-pulse">
        <div className="h-6 bg-muted rounded w-1/3 mb-6"></div>
        <div className="space-y-3">
          {[1, 2, 3, 4, 5]?.map((i) => (
            <div key={i} className="h-16 bg-muted rounded"></div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded-xl shadow-sm overflow-hidden">
      <div className="p-4 md:p-6 border-b border-border">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <h3 className="text-lg md:text-xl font-semibold text-foreground">Merchant Performance</h3>
          <div className="flex items-center gap-2 flex-wrap">
            {selectedMerchants?.length > 0 && (
              <span className="text-xs md:text-sm text-muted-foreground">
                {selectedMerchants?.length} selected
              </span>
            )}
            <Button variant="outline" size="sm" iconName="Filter">
              Filter
            </Button>
            <Button variant="outline" size="sm" iconName="Download">
              Export
            </Button>
          </div>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-muted/50 border-b border-border">
            <tr>
              <th className="px-4 py-3 text-left">
                <input
                  type="checkbox"
                  checked={selectedMerchants?.length === paginatedData?.length}
                  onChange={handleSelectAll}
                  className="rounded border-border"
                />
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Merchant
              </th>
              <th 
                className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider cursor-pointer hover:text-foreground"
                onClick={() => handleSort('transactionVolume')}
              >
                <div className="flex items-center gap-1">
                  Transactions
                  <Icon name={sortConfig?.key === 'transactionVolume' ? (sortConfig?.direction === 'asc' ? 'ChevronUp' : 'ChevronDown') : 'ChevronsUpDown'} size={14} />
                </div>
              </th>
              <th 
                className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider cursor-pointer hover:text-foreground"
                onClick={() => handleSort('revenue')}
              >
                <div className="flex items-center gap-1">
                  Revenue
                  <Icon name={sortConfig?.key === 'revenue' ? (sortConfig?.direction === 'asc' ? 'ChevronUp' : 'ChevronDown') : 'ChevronsUpDown'} size={14} />
                </div>
              </th>
              <th 
                className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider cursor-pointer hover:text-foreground"
                onClick={() => handleSort('lastActivity')}
              >
                <div className="flex items-center gap-1">
                  Last Activity
                  <Icon name={sortConfig?.key === 'lastActivity' ? (sortConfig?.direction === 'asc' ? 'ChevronUp' : 'ChevronDown') : 'ChevronsUpDown'} size={14} />
                </div>
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Status
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Risk
              </th>
              <th className="px-4 py-3 text-right text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {paginatedData?.map((merchant) => (
              <tr key={merchant?.id} className="hover:bg-muted/30 transition-colors">
                <td className="px-4 py-4">
                  <input
                    type="checkbox"
                    checked={selectedMerchants?.includes(merchant?.id)}
                    onChange={() => handleSelectMerchant(merchant?.id)}
                    className="rounded border-border"
                  />
                </td>
                <td className="px-4 py-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <span className="text-sm font-semibold text-primary">
                        {merchant?.name?.substring(0, 2)?.toUpperCase()}
                      </span>
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-foreground truncate">{merchant?.name}</p>
                      <p className="text-xs text-muted-foreground truncate">{merchant?.category}</p>
                    </div>
                  </div>
                </td>
                <td className="px-4 py-4">
                  <p className="text-sm font-medium text-foreground data-text">
                    {merchant?.transactionVolume?.toLocaleString('id-ID')}
                  </p>
                  <p className="text-xs text-muted-foreground">transactions</p>
                </td>
                <td className="px-4 py-4">
                  <p className="text-sm font-medium text-foreground data-text">
                    {formatCurrency(merchant?.revenue)}
                  </p>
                  <p className="text-xs text-success">+{merchant?.revenueGrowth}%</p>
                </td>
                <td className="px-4 py-4">
                  <p className="text-sm text-foreground">{formatDate(merchant?.lastActivity)}</p>
                  <p className="text-xs text-muted-foreground">{merchant?.lastActivityTime}</p>
                </td>
                <td className="px-4 py-4">
                  {getStatusBadge(merchant?.status)}
                </td>
                <td className="px-4 py-4">
                  {getRiskBadge(merchant?.riskLevel)}
                </td>
                <td className="px-4 py-4">
                  <div className="flex items-center justify-end gap-2">
                    <button className="p-2 hover:bg-muted rounded-lg transition-colors" title="View Details">
                      <Icon name="Eye" size={16} className="text-muted-foreground" />
                    </button>
                    <button className="p-2 hover:bg-muted rounded-lg transition-colors" title="Edit">
                      <Icon name="Edit" size={16} className="text-muted-foreground" />
                    </button>
                    <button className="p-2 hover:bg-muted rounded-lg transition-colors" title="More Options">
                      <Icon name="MoreVertical" size={16} className="text-muted-foreground" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="p-4 md:p-6 border-t border-border">
        <div className="flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-sm text-muted-foreground">
            Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, sortedData?.length)} of {sortedData?.length} merchants
          </p>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              iconName="ChevronLeft"
              disabled={currentPage === 1}
              onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
            >
              Previous
            </Button>
            <div className="flex items-center gap-1">
              {[...Array(Math.min(5, totalPages))]?.map((_, i) => {
                const pageNum = i + 1;
                return (
                  <button
                    key={pageNum}
                    onClick={() => setCurrentPage(pageNum)}
                    className={`w-8 h-8 rounded-lg text-sm font-medium transition-colors ${
                      currentPage === pageNum
                        ? 'bg-primary text-primary-foreground'
                        : 'hover:bg-muted text-foreground'
                    }`}
                  >
                    {pageNum}
                  </button>
                );
              })}
            </div>
            <Button
              variant="outline"
              size="sm"
              iconName="ChevronRight"
              iconPosition="right"
              disabled={currentPage === totalPages}
              onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
            >
              Next
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MerchantPerformanceTable;